import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Paginator from './paginator';

type HookProps = {
  countsPerPages: number[];
  dataLength: number;
  initPerPage?: number;
  initCurrentPage?: number;
  onChange?: () => void;
  className?: string;
  skipInit?: boolean;
};

type HookResult = {
  currentPage: number;
  countPerPage: number;
  paginator: React.ReactNode;
};

export const usePaginator = ({
  countsPerPages,
  dataLength,
  initPerPage,
  initCurrentPage = 1,
  onChange,
  className,
  skipInit = false,
}: HookProps): HookResult => {
  const [currentPage, setCurrentPage] = useState(initCurrentPage);
  const [countPerPage, setCountPerPage] = useState(
    initPerPage || countsPerPages[0],
  );
  const lastPage = useMemo(
    () => Math.trunc((dataLength - 1) / countPerPage) + 1,
    [dataLength, countPerPage],
  );
  const initRef = useRef(false);

  useEffect(() => {
    if (!initRef.current) {
      if (skipInit) {
        return;
      }

      initRef.current = true;
      return;
    }

    setCurrentPage(1);
  }, [dataLength, skipInit]);

  const setPrevPage = useCallback(() => {
    if (currentPage === 1) {
      return;
    }

    setCurrentPage((prev) => prev - 1);
    onChange?.();
  }, [currentPage, onChange]);

  const setNextPage = useCallback(() => {
    if (currentPage === lastPage) {
      return;
    }

    setCurrentPage((prev) => prev + 1);
    onChange?.();
  }, [currentPage, lastPage, onChange]);

  const changeCountPerPage = useCallback(
    (value: number) => {
      if (countPerPage === value) {
        return;
      }

      setCurrentPage(1);
      setCountPerPage(value);
      onChange?.();
    },
    [countPerPage, onChange],
  );

  const selectPage = useCallback(
    (value: number) => {
      if (currentPage === value) {
        return;
      }

      setCurrentPage(value);
      onChange?.();
    },
    [currentPage, onChange],
  );

  return {
    currentPage,
    countPerPage,
    paginator: (
      <Paginator
        className={className}
        currentPage={currentPage}
        lastPage={lastPage}
        countPerPage={countPerPage}
        countsPerPages={countsPerPages}
        setPrevPage={setPrevPage}
        setNextPage={setNextPage}
        changeCountPerPage={changeCountPerPage}
        selectPage={selectPage}
      />
    ),
  };
};

export default usePaginator;
